/**
 * Composable for Barcode Label Printing
 * 
 * Provides a convenient Vue 3 composable wrapper around the barcode label service.
 * Use this in Vue components for reactive printing functionality with loading states.
 */

import { ref } from 'vue';
import {
  printStockBarcodeLabel,
  printBSURBarcodeLabel,
  printProductBarcode,
  batchPrintProductBarcodes,
  isThermalPrintingAvailable,
  getCurrentPrinterConfig
} from '@/services/barcodeLabelService';

/**
 * Composable for barcode label printing with reactive state management
 * @returns {Object} Printing functions and reactive state
 */
export function useBarcodeLabel() {
  const isPrinting = ref(false);
  const lastPrintResult = ref(null);
  const printError = ref(null);

  /**
   * Print stock barcode label(s) with loading state
   */
  const printStock = async (options) => {
    isPrinting.value = true;
    printError.value = null;
    
    try {
      const result = await printStockBarcodeLabel(options);
      lastPrintResult.value = result;
      
      if (!result.success) {
        printError.value = result.error;
      }
      
      return result;
    } catch (error) {
      printError.value = error.message;
      return { success: false, error: error.message };
    } finally {
      isPrinting.value = false;
    }
  };

  /**
   * Print BSUR barcode label with loading state
   */
  const printBSUR = async (options) => {
    isPrinting.value = true;
    printError.value = null;
    
    try {
      const result = await printBSURBarcodeLabel(options);
      lastPrintResult.value = result;
      
      if (!result.success) {
        printError.value = result.error;
      }
      
      return result;
    } catch (error) {
      printError.value = error.message;
      return { success: false, error: error.message };
    } finally {
      isPrinting.value = false;
    }
  };

  /**
   * Print product barcode with loading state
   */
  const printProduct = async (product) => {
    isPrinting.value = true;
    printError.value = null;
    
    try {
      const result = await printProductBarcode(product);
      lastPrintResult.value = result;
      
      if (!result.success) {
        printError.value = result.error;
      }
      
      return result;
    } catch (error) {
      printError.value = error.message;
      return { success: false, error: error.message };
    } finally {
      isPrinting.value = false;
    }
  };

  /**
   * Batch print multiple product barcodes with loading state
   */
  const printBatch = async (products, options = {}) => {
    isPrinting.value = true;
    printError.value = null;
    
    try {
      const result = await batchPrintProductBarcodes(products, options);
      lastPrintResult.value = result;
      
      if (!result.success) {
        printError.value = result.error;
      }
      
      return result;
    } catch (error) {
      printError.value = error.message;
      return { success: false, error: error.message };
    } finally {
      isPrinting.value = false;
    }
  };

  /**
   * Clear error state
   */
  const clearError = () => {
    printError.value = null;
  };

  /**
   * Reset all state
   */
  const reset = () => {
    isPrinting.value = false;
    lastPrintResult.value = null;
    printError.value = null;
  };

  return {
    // State
    isPrinting,
    lastPrintResult,
    printError,
    
    // Actions
    printStock,
    printBSUR,
    printProduct,
    printBatch,
    
    // Utilities
    clearError,
    reset,
    isThermalPrintingAvailable,
    getCurrentPrinterConfig
  };
}

export default useBarcodeLabel;
